import { useEntity } from '@/hooks/queries/useEntityQuery';
import { controlServiceAdapter } from '@/services/adapters/controlServiceAdapter';
import { ControlType } from '@/types/floorPlanViewer';
import { useMemo } from 'react';

export function useControlsStandardized(floorPlanId: string) {
  const {
    data,
    isLoading,
    error,
    refetch,
    create,
    update,
    delete: deleteControl,
    ...mutations
  } = useEntity<ControlType>('lighting_controls', floorPlanId, controlServiceAdapter);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data) return { totalControls: 0, typeStats: {} };

    const totalControls = data.length;

    const typeStats = data.reduce(
      (acc, control) => {
        acc[control.type] = (acc[control.type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { totalControls, typeStats };
  }, [data]);

  // Enhanced create function with floor plan assignment and sequence number calculation
  const createControl = async (
    controlData: Partial<ControlType>,
    options?: { showSuccessToast?: boolean },
  ) => {
    let enhancedControlData: Partial<ControlType> & Record<string, any> = {
      ...controlData,
      floor_plan_id: floorPlanId,
    };

    const x =
      (controlData as any).x ?? (controlData as any).xPosition ?? (controlData as any).x_position;
    const y =
      (controlData as any).y ?? (controlData as any).yPosition ?? (controlData as any).y_position;

    if (typeof x === 'number') {
      enhancedControlData.x = x;
      enhancedControlData.x_position = x;
    }
    if (typeof y === 'number') {
      enhancedControlData.y = y;
      enhancedControlData.y_position = y;
    }

    // Calculate sequence_number for centralized keypads
    if (controlData.type === 'centralized-keypad') {
      try {
        // Import Supabase client
        const { getAuthenticatedSupabaseClient } = await import('@/integrations/supabase/client');
        const supabase = await getAuthenticatedSupabaseClient();

        // Get the project_id for this floor plan
        const { data: floorPlanData } = await supabase
          .from('floor_plans')
          .select('project_id')
          .eq('id', floorPlanId)
          .single();

        if (floorPlanData?.project_id) {
          // Get all floor plans in this project
          const { data: projectFloorPlans } = await supabase
            .from('floor_plans')
            .select('id')
            .eq('project_id', floorPlanData.project_id);

          if (projectFloorPlans && projectFloorPlans.length > 0) {
            const floorPlanIds = projectFloorPlans.map(fp => fp.id);

            // Get all centralized keypads in this project
            const { data: allKeypads } = await supabase
              .from('lighting_controls')
              .select('sequence_number')
              .eq('type', 'centralized-keypad')
              .in('floor_plan_id', floorPlanIds)
              .order('sequence_number', { ascending: true });

            if (allKeypads && allKeypads.length > 0) {
              const usedNumbers = allKeypads
                .map(k => k.sequence_number)
                .filter(num => num !== null);

              // Find the first gap or use max + 1
              let sequenceNumber = 1;
              let foundGap = false;
              for (let i = 1; i <= usedNumbers.length + 1; i++) {
                if (!usedNumbers.includes(i)) {
                  sequenceNumber = i;
                  foundGap = true;
                  break;
                }
              }
              if (!foundGap) {
                sequenceNumber = Math.max(...usedNumbers) + 1;
              }

              enhancedControlData = {
                ...enhancedControlData,
                sequence_number: sequenceNumber,
              };
            } else {
              // No existing keypads, start with 1
              enhancedControlData = {
                ...enhancedControlData,
                sequence_number: 1,
              };
            }
          }
        }
      } catch (error) {
        // Import logger
        const { logger } = await import('@/lib/logger');
        logger.error('Error calculating sequence number for centralized keypad:', error);
        // Fall back to sequence_number: 1
        enhancedControlData = {
          ...enhancedControlData,
          sequence_number: 1,
        };
      }
    }

    return options ? create(enhancedControlData, options) : create(enhancedControlData);
  };

  // Enhanced update function
  const updateControl = async (
    id: string,
    updates: Partial<ControlType>,
    options?: { showSuccessToast?: boolean },
  ) => {
    return update({ id, updates, showSuccessToast: options?.showSuccessToast });
  };

  // Bulk operations
  const assignToCircuit = async (controlIds: string[], circuitId: string | null) => {
    const promises = controlIds.map(id =>
      updateControl(id, { circuit_id: circuitId }, { showSuccessToast: false }),
    );
    return Promise.all(promises);
  };

  const updateControlPositions = async (updates: { id: string; x: number; y: number }[]) => {
    const promises = updates.map(({ id, x, y }) =>
      updateControl(id, { x, y }, { showSuccessToast: false }),
    );
    return Promise.all(promises);
  };

  return {
    // Data
    controls: data || [],

    // Loading states
    isLoading,
    error,

    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,

    // Operations
    createControl,
    updateControl,
    deleteControl,
    refetch,

    // Bulk operations
    assignToCircuit,
    updateControlPositions,

    // Statistics
    totalControls: statistics.totalControls,
    getControlTypeStats: () => statistics.typeStats,
  };
}
